<script setup lang="ts">
    import { useAuthStore } from '@/stores/auth.store';
    import type { SidebarMenus } from '@/types/components/sidebar';
    import { HomeRouteName } from '@/types/router/name/home';
    import { PhArrowUDownLeft } from '@phosphor-icons/vue';
    import { ref, watchEffect } from 'vue';
    import { useRoute } from 'vue-router';

    const authStore = useAuthStore();

    const props = defineProps<SidebarMenus>();

    const route = useRoute();
    const openGroup = ref<string | null>(route.meta.group as string);

    type MenuChild = { label?: string; path?: string; permission?: string };
    type MenuItem = {
        label?: string;
        path?: string;
        icon?: object;
        child?: MenuChild[];
        group?: string;
        permission?: string;
    };

    const getVisibleChildren = (children?: MenuChild[]): { label: string; path: string }[] => {
        if (!children || children.length === 0) return [];
        return children
            .filter((childItem: MenuChild) => {
                const hasLabelAndPath =
                    typeof childItem.label === 'string' && typeof childItem.path === 'string';
                const hasPermission = childItem.permission
                    ? authStore.canAccess(childItem.permission)
                    : true;
                return hasLabelAndPath && hasPermission;
            })
            .map(c => ({ label: c.label as string, path: c.path as string }));
    };
    const canShowMenu = (item: MenuItem) => {
        if (item.child && item.child.length > 0) {
            const visibleChildren = getVisibleChildren(item.child);

            if (visibleChildren.length === 0) {
                return false;
            }
        }

        if (item.permission) {
            return authStore.canAccess(item.permission);
        }

        return true;
    };

    watchEffect(() => {
        // Auto open the active route's group
        if (route.meta.group) {
            openGroup.value = route.meta.group as string;
        }
    });

    const toggleGroup = (group: string) => {
        openGroup.value = openGroup.value === group ? null : group;
    };
</script>

<template>
    <div class="flex flex-col gap-2 h-full">
        <!-- TITLE PROFILE -->
        <div class="flex items-center justify-center py-1" v-if="props.settings">
            <b
                class="text-brand-500 text-center inline-flex items-center justify-center gap-2.5 p-3 pr-6 rounded-lg hover:bg-brand-50 cursor-pointer transition-colors duration-200"
                @click="$router.push({ name: HomeRouteName.Home })"
            >
                <PhArrowUDownLeft size="24" />
                Kembali ke Beranda
            </b>
        </div>

        <!-- TITLE DEFAULT -->
        <div class="flex-center pt-1 pb-3.5 px-5" v-else>
            <IconsLogo
                @click="$router.push({ name: HomeRouteName.Home })"
                class="cursor-pointer shrink-0"
            />
        </div>

        <div class="flex flex-col h-full overflow-y-auto">
            <!-- MENU ITEMS -->
            <!-- <span
                v-if="userStore.count > 0 && settings"
                class="bg-error-300 rounded-full w-4 h-4 justify-center absolute flex left-[260px] top-[188.2px] animate-ping z-20"
            ></span>
            <span
                class="bg-error-100 rounded-full text-error-600 w-6 h-6 justify-center absolute flex left-64 top-[184px] z-20"
                v-if="userStore.count > 0 && settings"
            >
                <b class="text-xs mt-1 ml-0.5">{{
                    userStore.count > 9 ? '9+' : userStore.count
                }}</b>
            </span> -->

            <template
                v-for="item in [...props.defaultMenu, ...props.subMenu] as Array<{
                    label: string;
                    path: string;
                    icon: object;
                    child: Array<{ label: string; path: string; permission?: string }>;
                    group: string;
                    permission?: string;
                }>"
                :key="item.path"
            >
                <ButtonMenu
                    v-if="canShowMenu(item)"
                    class="mb-2"
                    :label="item.label"
                    :icon="item.icon"
                    :path="item.path"
                    :children="getVisibleChildren(item.child)"
                    :group="item.group"
                    :is-open="openGroup === item.group"
                    @toggle="toggleGroup(item.group)"
                />
            </template>
        </div>
    </div>
</template>

<style scoped>
    .fade-slide-enter-active,
    .fade-slide-leave-active {
        transition: all 0.1s ease;
    }
    .fade-slide-enter-from {
        opacity: 0;
        transform: translateY(-10px);
    }
    .fade-slide-enter-to {
        opacity: 1;
        transform: translateY(0);
    }
    .fade-slide-leave-from {
        opacity: 1;
        transform: translateY(0);
    }
    .fade-slide-leave-to {
        opacity: 0;
        transform: translateY(-10px);
    }
</style>
